import { radToDeg } from './utils.js';

export function generateExplanationModule(ac1, ac2, intersection, solution) {
    const dist1 = Math.sqrt(
        Math.pow(intersection.x - ac1.x, 2) + Math.pow(intersection.y - ac1.y, 2),
    );
    const dist2 = Math.sqrt(
        Math.pow(intersection.x - ac2.x, 2) + Math.pow(intersection.y - ac2.y, 2),
    );
    const distDiff = Math.abs(dist1 - dist2);
    const closerAircraft = dist1 < dist2 ? ac1 : ac2;
    const furtherAircraft = dist1 < dist2 ? ac2 : ac1;
    const speedDiff = Math.abs(ac1.speed - ac2.speed);
    const fasterAircraft = ac1.speed > ac2.speed ? ac1 : ac2;
    const timeToIntersection =
        (dist1 < dist2 ? dist1 : dist2) / (closerAircraft.speed / 60);
    const speedAdjustment = (speedDiff / 60) * timeToIntersection;

    let combinedDist;
    let speedEffectHTML;
    let minutesHeuristic = '';

    if (speedDiff >= 51) {
        const milesPerMinute = (speedDiff / 60).toFixed(1);
        minutesHeuristic = ` (${milesPerMinute} NM per <b>1 min</b>)`;
    } else if (speedDiff >= 40) {
        minutesHeuristic = ' (1 NM every <b>1.5 min</b>)';
    } else if (speedDiff > 0) {
        const minutesPerMile = Math.round(60 / speedDiff);
        if (minutesPerMile > 0) {
            minutesHeuristic = ` (1 NM every <b>${minutesPerMile} min</b>)`;
        }
    }

    const isOvertake =
        fasterAircraft.callsign === furtherAircraft.callsign &&
        speedAdjustment > distDiff;

    if (isOvertake) {
        combinedDist = speedAdjustment - distDiff;
        speedEffectHTML = `The speed difference of <b class="text-yellow-400">${speedDiff.toFixed(0)} kt</b>${minutesHeuristic} allows ${fasterAircraft.callsign} to overtake ${closerAircraft.callsign} over <b class="text-yellow-400">${timeToIntersection.toFixed(0)} minutes</b>, creating a new gap of <b class="text-yellow-400">~${combinedDist.toFixed(1)} NM</b>.`;
    } else {
        let phrase;
        if (fasterAircraft.callsign === furtherAircraft.callsign) {
            combinedDist = distDiff - speedAdjustment;
            phrase = 'closes the gap';
        } else {
            combinedDist = distDiff + speedAdjustment;
            phrase = 'extends the lead';
        }
        speedEffectHTML = `The speed difference of <b class="text-yellow-400">${speedDiff.toFixed(0)} kt</b>${minutesHeuristic} over <b class="text-yellow-400">${timeToIntersection.toFixed(0)} minutes</b> ${phrase} by <b class="text-yellow-400">~${speedAdjustment.toFixed(1)} NM</b>, resulting in a separation of <b class="text-yellow-400">~${combinedDist.toFixed(1)} NM</b> at the crossing point.`;
    }

    const headingDiffRad = Math.abs(ac1.headingRad - ac2.headingRad);
    const crossingAngleRad =
        headingDiffRad > Math.PI ? 2 * Math.PI - headingDiffRad : headingDiffRad;
    const crossingAngleDeg = radToDeg(crossingAngleRad);

    const a = 0.00002469;
    const b = 0.00111;
    const reductionFactor = a * crossingAngleDeg * crossingAngleDeg + b * crossingAngleDeg;
    const finalApproximation = combinedDist * (1.0 - reductionFactor);

    const reductionPercent = reductionFactor * 100;
    const roundedReductionPercent = Math.round(reductionPercent / 5) * 5;

    let angleAdjective;
    if (crossingAngleDeg < 45) {
        angleAdjective = 'very shallow';
    } else if (crossingAngleDeg < 75) {
        angleAdjective = 'shallow';
    } else {
        angleAdjective = 'wide';
    }

    return `
        <h4 class="font-bold text-cyan-400 mb-2 text-base">💡 Approximation Breakdown</h4>
        <ol class="list-decimal list-inside space-y-2">
            <li>
                <strong>Initial Gap:</strong> ${closerAircraft.callsign} starts <b class="text-yellow-400">${distDiff.toFixed(1)} NM</b> closer to the intersection.
            </li>
            <li>
                <strong>Speed & Time Effect:</strong> ${speedEffectHTML}
            </li>
            <li>
                <strong>Angle Factor:</strong> The <b class="text-yellow-400">${angleAdjective} ${crossingAngleDeg.toFixed(0)}°</b> crossing angle reduces this separation by roughly <b class="text-yellow-400">${roundedReductionPercent}%</b>.
            </li>
        </ol>
        <h3 class="text-center mt-3 font-semibold">
            Estimated Minimum Distance: <span class="text-green-400 text-base">${finalApproximation.toFixed(1)} NM</span>
            <span class="text-xs text-gray-400 ml-1">(Actual: ${solution.minDistance.toFixed(1)} NM)</span>
        </h3>
    `;
}
